<template>
  <div class="Steps">
    <div v-if="hasListedSteps" class="list">
      <div
        v-for="step in steps"
        :key="step.index"
        class="step"
        :class="{
          active: step.index === activeIndex,
          jumpable: canJumpTo(step.index),
        }"
        @click="jumpTo(step.index)"
        @keypress.enter="jumpTo(step.index)">
        <span>
          {{ step.index + 1 }}.
        </span>
        <strong>
          {{ step.title }}
        </strong>
      </div>
    </div>

    <FormValidation @submit="next">
      <transition mode="out-in" :name="animation">
        <div :key="activeStep.index" class="current" :class="`Step-${activeIndex}`">
          <slot v-bind="{ jumpTo, next }" :name="activeStep.index" />
        </div>
      </transition>
      <div class="navigation">
        <slot name="notice" />
        <Row v-if="activeStep.hasNavigation">
          <Column :md="6">
            <slot v-if="hasSlot('back')" :name="`${activeIndex}-back`" />
            <Btn v-else-if="activeIndex" key="back" @click="back">
              {{ $t('previous') }}
            </Btn>
          </Column>
          <Column :md="6">
            <slot v-if="hasSlot('next')" :name="`${activeIndex}-next`" />
            <Btn v-else key="next" type="submit">
              {{ $t('next') }}
            </Btn>
            <slot v-if="hasSlot('nextAfter')" :name="`${activeIndex}-nextAfter`" />
          </Column>
        </Row>
      </div>
    </FormValidation>
  </div>
</template>

<script>
export default {
  props: {
    hasListedSteps: Boolean,
  },
  data() {
    return {
      activeIndex: 0,
      isGoingBack: false,
    }
  },
  computed: {
    activeStep() {
      return this.steps.find(({ index }) => index === this.activeIndex) || {}
    },
    animation() {
      return this.isGoingBack ? 'slide-back' : 'slide-forward'
    },
    lastStepIndex() {
      return Math.max(...this.steps.map(({ index }) => index))
    },
    slotsKeys() {
      return Object.keys(this.$slots)
    },
    steps() {
      return Object
        .entries(this.$scopedSlots)
        .map(([key, slotFactory]) => [
          key,
          slotFactory(),
        ])
        .filter(([key]) => !isNaN(Number(key)))
        .map(([key, [slot]]) => {
          const { hasNavigation, title } = slot.data.attrs

          return {
            hasNavigation: typeof hasNavigation === 'boolean'
              ? hasNavigation
              : true,
            index: Number(key),
            title,
          }
        })
    },
  },
  methods: {
    back() {
      this.jumpTo(this.activeIndex - 1)
    },
    canJumpTo(stepIndex) {
      return stepIndex < this.activeIndex
    },
    hasSlot(type) {
      return this.slotsKeys.includes(`${this.activeIndex}-${type}`)
    },
    jumpTo(stepIndex) {
      if (!this.canJumpTo(stepIndex)) return

      if (stepIndex < this.activeIndex) {
        this.isGoingBack = true
        setTimeout(() => {
          this.isGoingBack = false
        }, 500)
      }

      this.activeIndex = stepIndex
    },
    next() {
      if (this.lastStepIndex === this.activeIndex) {
        this.$emit('submit')
        return
      }

      this.activeIndex += 1
    },
  },
}
</script>

<style lang="scss">
.Steps {
  .list {
    display: flex;
    margin-bottom: $grid-gutter-width;
    overflow: hidden;
    text-align: center;

    > .step {
      border-bottom: 1px solid transparent;
      flex-grow: 1;
      padding: 1rem;

      &.active {
        border-color: $main;
        color: $main;
      }

      &.jumpable {
        border-color: $border-color;
        cursor: pointer;

        &:hover {
          background: $main-lighter;
        }
      }
    }
  }

  .navigation {
    margin-top: 2rem;

    .Column {
      margin-bottom: 0.5rem;
      @include md {
        margin-bottom: 0;
      }
    }
  }
}
</style>

<i18n>
{
  "en": {
    "previous": "Go back",
    "next": "Continue"
  }
}
</i18n>
